import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'

const PALETTE: ReadonlyArray<string> = [
  'var(--steel-blue)',
  'var(--success)',
  'var(--warning)',
  'var(--info)',
]

// Pre-rolled crumpled-paper silhouettes — irregular polygons that read
// as torn paper scraps rather than circles. Each scrap chooses one
// at random.
const CLIP_PATHS: ReadonlyArray<string> = [
  'polygon(20% 0%, 70% 8%, 92% 30%, 100% 70%, 78% 96%, 35% 100%, 8% 75%, 0% 35%)',
  'polygon(10% 12%, 55% 0%, 90% 18%, 100% 55%, 80% 88%, 42% 100%, 0% 78%, 4% 38%)',
  'polygon(30% 3%, 78% 14%, 96% 48%, 88% 82%, 50% 96%, 14% 88%, 0% 50%, 12% 18%)',
  'polygon(5% 22%, 45% 4%, 82% 10%, 96% 42%, 90% 78%, 60% 96%, 22% 90%, 0% 58%)',
]

const PARTICLE_COUNT = 22
const ANIMATION_MS = 1600

interface Particle {
  id: number
  color: string
  clipPath: string
  startXVw: number          // viewport-width % at top
  endXVw: number            // viewport-width % at bottom
  fallDistance: number      // px below origin
  rotateStart: number       // deg
  rotateEnd: number         // deg
  width: number             // px
  height: number            // px (slightly different → rectangular feel)
  delayMs: number
}

export interface UseConfettiResult {
  pop: () => void
  ConfettiPortal: JSX.Element | null
}

function _rand(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

export function useConfetti(): UseConfettiResult {
  const [particles, setParticles] = useState<Particle[]>([])
  const counterRef = useRef(0)
  // Pending per-batch cleanup timers, cleared on unmount so none fires
  // setParticles after the component (or a test's jsdom) is gone.
  const timersRef = useRef<Set<number>>(new Set())

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach((id) => window.clearTimeout(id))
      timers.clear()
    }
  }, [])

  const pop = useCallback(() => {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
    ) {
      return
    }
    const batch = counterRef.current++
    const next: Particle[] = Array.from({ length: PARTICLE_COUNT }, (_, i) => {
      const startXVw = _rand(15, 85) // spread across most of the viewport width
      const sway = _rand(-12, 12)
      return {
        id: batch * PARTICLE_COUNT + i,
        color: PALETTE[Math.floor(Math.random() * PALETTE.length)] ?? PALETTE[0]!,
        clipPath: CLIP_PATHS[Math.floor(Math.random() * CLIP_PATHS.length)] ?? CLIP_PATHS[0]!,
        startXVw,
        endXVw: startXVw + sway,
        fallDistance: window.innerHeight * _rand(0.55, 0.85),
        rotateStart: _rand(-180, 180),
        rotateEnd: _rand(-180, 180) + (Math.random() < 0.5 ? -540 : 540), // tumble
        width: _rand(8, 14),
        height: _rand(10, 16),
        delayMs: Math.floor(_rand(0, 220)),
      }
    })
    setParticles((prev) => [...prev, ...next])
    const longestLifetime = ANIMATION_MS + 250 // enough headroom for any delay
    const timerId = window.setTimeout(() => {
      timersRef.current.delete(timerId)
      setParticles((prev) => prev.filter((p) => !next.includes(p)))
    }, longestLifetime)
    timersRef.current.add(timerId)
  }, [])

  const ConfettiPortal: JSX.Element | null =
    typeof document === 'undefined' || particles.length === 0
      ? null
      : createPortal(
          <div
            data-testid="confetti-portal"
            style={{
              position: 'fixed',
              inset: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
              zIndex: 9999,
            }}
          >
            {particles.map((p) => (
              <span
                key={p.id}
                style={{
                  position: 'absolute',
                  left: `${p.startXVw}vw`,
                  top: '-24px', // start just above the viewport
                  width: `${p.width}px`,
                  height: `${p.height}px`,
                  background: p.color,
                  clipPath: p.clipPath,
                  WebkitClipPath: p.clipPath,
                  willChange: 'transform, opacity',
                  animation: `confetti-fall ${ANIMATION_MS}ms cubic-bezier(0.4, 0.05, 0.55, 1) ${p.delayMs}ms forwards`,
                  ['--start-rotate' as string]: `${p.rotateStart}deg`,
                  ['--end-rotate' as string]: `${p.rotateEnd}deg`,
                  ['--fall' as string]: `${p.fallDistance}px`,
                  ['--sway' as string]: `${p.endXVw - p.startXVw}vw`,
                } as CSSProperties}
              />
            ))}
            <style>{`
              @keyframes confetti-fall {
                0% {
                  transform: translate(0, 0) rotate(var(--start-rotate));
                  opacity: 0;
                }
                12% {
                  opacity: 1;
                }
                85% {
                  opacity: 1;
                }
                100% {
                  transform: translate(var(--sway), var(--fall)) rotate(var(--end-rotate));
                  opacity: 0;
                }
              }
            `}</style>
          </div>,
          document.body,
        )

  return { pop, ConfettiPortal }
}
